import React, { useState } from 'react';
import { Button, Card, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, SplitCellsOutlined } from '@ant-design/icons';
import type { DailyCurrency, DailyDayConfig, DailyDayType, DailyMultiplierBand } from '@/types';
import {
  CURRENCY_LABELS,
  DAILY_CURRENCIES,
  DAILY_CYCLE_LENGTH,
  DAILY_DAY_TYPES,
  DAY_TYPE_HELP,
  DAY_TYPE_LABELS,
  DIVISIONS,
  RARITY_LABELS,
  baseAmount,
  coinBonus,
  computeDayReward,
  dayBaseAmounts,
  errorsUnder,
  removeRange,
  setRangeEnd,
  splitRange,
  TAB_PREFIXES,
  withDayType,
} from './dailyEngagementConfig';
import { useDraft } from './draftContext';
import { CardListEditor, ErrorList, LabeledNumber, NumberInput } from './fields';
import { Day28CardPicker } from './Day28CardPicker';

const { Text, Paragraph } = Typography;

const DAY_KEYS = { from: 'fromDay', to: 'toDay' } as const;

const fmt = (n: number) => (Number.isFinite(n) ? n.toLocaleString() : '?');

const DAY_TYPE_COLORS: Record<DailyDayType, string> = {
  normal: 'default',
  card_chance: 'purple',
  guaranteed_milestone: 'blue',
  enhanced_milestone: 'gold',
  grand_milestone: 'red',
};

/** Daily Reward: base formulas, caps, multiplier bands, the 28-day table, Day 28 card, coin bonus. */
export const DailyBonusTab: React.FC = () => {
  const { draft, editable, errors, update } = useDraft();
  const [division, setDivision] = useState(1);

  const formulaRows = DAILY_CURRENCIES.map((currency) => ({ currency }));

  const setBand = (i: number, change: Partial<DailyMultiplierBand>) =>
    update((d) => {
      d.multiplierBands[i] = { ...d.multiplierBands[i], ...change };
    });

  const setDay = (i: number, next: DailyDayConfig) =>
    update((d) => {
      d.days[i] = next;
    });

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <ErrorList prefixes={TAB_PREFIXES.daily} />

      <Card size="small" title="Base rewards and caps">
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Amount at a division = <Text code>base + perStep × floor(division ÷ divisor)</Text>. Then
          × the streak multiplier, rounded down, then capped. Cards are never multiplied. A player
          with no division counts as division 1.
        </Paragraph>
        <Table
          size="small"
          pagination={false}
          rowKey="currency"
          dataSource={formulaRows}
          columns={[
            {
              title: 'Currency',
              render: (_v, { currency }) => <Text strong>{CURRENCY_LABELS[currency]}</Text>,
            },
            {
              title: 'Base',
              render: (_v, { currency }) => (
                <NumberInput
                  path={`baseRewards.${currency}.base`}
                  value={draft.baseRewards[currency].base}
                  min={0}
                  onChange={(v) =>
                    update((d) => {
                      d.baseRewards[currency].base = v;
                    })
                  }
                />
              ),
            },
            {
              title: 'Per step',
              render: (_v, { currency }) => (
                <NumberInput
                  path={`baseRewards.${currency}.perStep`}
                  value={draft.baseRewards[currency].perStep}
                  min={0}
                  onChange={(v) =>
                    update((d) => {
                      d.baseRewards[currency].perStep = v;
                    })
                  }
                />
              ),
            },
            {
              title: 'Divisor (divisions per step)',
              render: (_v, { currency }) => (
                <NumberInput
                  path={`baseRewards.${currency}.divisor`}
                  value={draft.baseRewards[currency].divisor}
                  min={1}
                  onChange={(v) =>
                    update((d) => {
                      d.baseRewards[currency].divisor = v;
                    })
                  }
                />
              ),
            },
            {
              title: 'Cap per claim',
              render: (_v, { currency }) => (
                <NumberInput
                  path={`caps.${currency}`}
                  value={draft.caps[currency]}
                  min={1}
                  onChange={(v) =>
                    update((d) => {
                      d.caps[currency] = v;
                    })
                  }
                />
              ),
            },
          ]}
        />
      </Card>

      <Card size="small" title="Enhanced bundle (enhanced and grand milestones)">
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Enhanced and grand milestone days grant all three currencies: coins = coins base × (100 +
          bonus %) ÷ 100, EvoTokens and Match Tokens = base + the bonus. Then the multiplier, round
          down, cap.
        </Paragraph>
        <Space wrap size="large" align="start">
          <LabeledNumber
            label="Coins bonus"
            path="enhancedBonus.coinsPercent"
            value={draft.enhancedBonus.coinsPercent}
            min={0}
            addonAfter="%"
            onChange={(v) =>
              update((d) => {
                d.enhancedBonus.coinsPercent = v;
              })
            }
          />
          <LabeledNumber
            label="EvoTokens bonus"
            path="enhancedBonus.evoTokens"
            value={draft.enhancedBonus.evoTokens}
            min={0}
            onChange={(v) =>
              update((d) => {
                d.enhancedBonus.evoTokens = v;
              })
            }
          />
          <LabeledNumber
            label="Match Tokens bonus"
            path="enhancedBonus.matchTokens"
            value={draft.enhancedBonus.matchTokens}
            min={0}
            onChange={(v) =>
              update((d) => {
                d.enhancedBonus.matchTokens = v;
              })
            }
          />
        </Space>
      </Card>

      <Card size="small" title="Per-division preview (before the multiplier)">
        <Table
          size="small"
          pagination={false}
          rowKey="division"
          scroll={{ x: true }}
          dataSource={DIVISIONS.map((div) => ({ division: div }))}
          columns={[
            { title: 'Division', dataIndex: 'division', width: 90 },
            ...DAILY_CURRENCIES.map((c: DailyCurrency) => ({
              title: `${CURRENCY_LABELS[c]} base`,
              key: c,
              render: (_v: unknown, { division: div }: { division: number }) =>
                fmt(baseAmount(draft, c, div)),
            })),
            {
              title: 'Enhanced bundle (coins / Evo / Match)',
              key: 'enhanced',
              render: (_v: unknown, { division: div }: { division: number }) => {
                const b = dayBaseAmounts(
                  draft,
                  { cycleDay: 0, dayType: 'enhanced_milestone', currency: null, cards: [] },
                  div
                );
                // Rounded down like the claim's baseCoins; the multiplier uses the exact value.
                return `${fmt(Math.floor(b.coins))} / ${fmt(b.evoTokens)} / ${fmt(b.matchTokens)}`;
              },
            },
          ]}
        />
      </Card>

      <Card size="small" title="Streak multiplier bands">
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Keyed on the streak day (the cycle day). Bands must run without gaps from day 1 to day 28;
          changing a band&apos;s last day moves the next band&apos;s first day. Multiplier 1 to 10,
          decimals allowed. It applies to currencies only, never to cards.
        </Paragraph>
        <Table
          size="small"
          pagination={false}
          rowKey="key"
          dataSource={draft.multiplierBands.map((b, key) => ({ ...b, key }))}
          columns={[
            {
              title: 'From day',
              render: (_v, b, i) => (
                <Tooltip
                  title={errorsUnder(errors, `multiplierBands[${i}].fromDay`).join('; ') || null}
                >
                  <Text
                    type={
                      errorsUnder(errors, `multiplierBands[${i}].fromDay`).length
                        ? 'danger'
                        : undefined
                    }
                  >
                    {b.fromDay}
                  </Text>
                </Tooltip>
              ),
            },
            {
              title: 'To day',
              render: (_v, b, i) => (
                <NumberInput
                  path={`multiplierBands[${i}].toDay`}
                  value={b.toDay}
                  min={1}
                  max={DAILY_CYCLE_LENGTH}
                  width={90}
                  onChange={(v) =>
                    update((d) => {
                      d.multiplierBands = setRangeEnd(d.multiplierBands, i, v, DAY_KEYS);
                    })
                  }
                />
              ),
            },
            {
              title: 'Multiplier',
              render: (_v, b, i) => (
                <NumberInput
                  path={`multiplierBands[${i}].multiplier`}
                  value={b.multiplier}
                  min={1}
                  max={10}
                  step={0.05}
                  integer={false}
                  addonAfter="×"
                  onChange={(v) => setBand(i, { multiplier: v })}
                />
              ),
            },
            {
              title: '',
              render: (_v, b, i) => (
                <Space size={4}>
                  <Tooltip title="Split this band in two">
                    <Button
                      size="small"
                      icon={<SplitCellsOutlined />}
                      disabled={!editable || !(b.toDay > b.fromDay)}
                      onClick={() =>
                        update((d) => {
                          d.multiplierBands = splitRange(d.multiplierBands, i, DAY_KEYS);
                        })
                      }
                    />
                  </Tooltip>
                  <Tooltip title="Remove this band (its neighbour takes over its days)">
                    <Button
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      disabled={!editable || draft.multiplierBands.length <= 1}
                      onClick={() =>
                        update((d) => {
                          d.multiplierBands = removeRange(d.multiplierBands, i, DAY_KEYS);
                        })
                      }
                    />
                  </Tooltip>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Card
        size="small"
        title="The 28-day cycle"
        extra={
          <Space>
            <Text type="secondary">Preview at division</Text>
            <Select<number>
              value={division}
              onChange={setDivision}
              style={{ width: 90 }}
              options={DIVISIONS.map((d) => ({ value: d, label: d }))}
            />
          </Space>
        }
      >
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Normal and card chance days grant one currency (card chance days also create the
          day&apos;s spin). Guaranteed milestones grant 1 to 3 cards only. Enhanced milestones grant
          the enhanced bundle. Grand milestones grant cards plus the bundle. The preview shows what
          each day grants at the chosen division when claimed on its own day;{' '}
          <Tag color="orange">capped</Tag> marks an amount the cap cut. The coin bonus column is the
          rewarded-video bonus on that day&apos;s coins.
        </Paragraph>
        <Table
          size="small"
          pagination={false}
          rowKey="cycleDay"
          scroll={{ x: true }}
          dataSource={draft.days}
          columns={[
            { title: 'Day', dataIndex: 'cycleDay', width: 55 },
            {
              title: 'Type',
              render: (_v, d, i) => (
                <Tooltip title={DAY_TYPE_HELP[d.dayType]}>
                  <Select<DailyDayType>
                    value={d.dayType}
                    disabled={!editable}
                    style={{ width: 200 }}
                    onChange={(t) => setDay(i, withDayType(d, t))}
                    options={DAILY_DAY_TYPES.map((t) => ({
                      value: t,
                      label: <Tag color={DAY_TYPE_COLORS[t]}>{DAY_TYPE_LABELS[t]}</Tag>,
                    }))}
                  />
                </Tooltip>
              ),
            },
            {
              title: 'Currency',
              render: (_v, d, i) =>
                d.dayType === 'normal' || d.dayType === 'card_chance' ? (
                  <Select<DailyCurrency>
                    value={d.currency ?? undefined}
                    disabled={!editable}
                    style={{ width: 140 }}
                    status={errorsUnder(errors, `days[${i}].currency`).length ? 'error' : undefined}
                    onChange={(currency) => setDay(i, { ...d, currency })}
                    options={DAILY_CURRENCIES.map((c) => ({ value: c, label: CURRENCY_LABELS[c] }))}
                  />
                ) : (
                  <Text type="secondary">
                    {d.dayType === 'guaranteed_milestone' ? 'None' : 'Enhanced bundle'}
                  </Text>
                ),
            },
            {
              title: 'Cards',
              render: (_v, d, i) =>
                d.dayType === 'guaranteed_milestone' || d.dayType === 'grand_milestone' ? (
                  <CardListEditor
                    path={`days[${i}].cards`}
                    value={d.cards}
                    onChange={(cards) => setDay(i, { ...d, cards })}
                  />
                ) : (
                  <Text type="secondary">None</Text>
                ),
            },
            {
              title: 'Multiplier',
              // Cards are never multiplied: a day with no currency shows none.
              render: (_v, d) => {
                const base = dayBaseAmounts(draft, d, division);
                return DAILY_CURRENCIES.some((c) => base[c] > 0)
                  ? `×${computeDayReward(draft, d, division).multiplier}`
                  : '—';
              },
            },
            {
              title: `Grants at division ${division}`,
              render: (_v, d) => {
                const r = computeDayReward(draft, d, division);
                const parts = DAILY_CURRENCIES.filter((c) => r.granted[c] > 0).map((c) => (
                  <Tag key={c} color={r.capped[c] ? 'orange' : undefined}>
                    {fmt(r.granted[c])} {CURRENCY_LABELS[c]}
                    {r.capped[c] ? ' (capped)' : ''}
                  </Tag>
                ));
                const cards = r.cards.map((c, j) => (
                  <Tag key={`card-${j}`} color="blue">
                    {c.count} × {RARITY_LABELS[c.rarity] ?? c.rarity}
                  </Tag>
                ));
                const all = [...parts, ...cards];
                return all.length > 0 ? (
                  <Space size={2} wrap>
                    {all}
                  </Space>
                ) : (
                  '—'
                );
              },
            },
            {
              title: 'Coin bonus (RV)',
              render: (_v, d) => {
                // The server refuses a bonus that rounds down to 0 (COIN_BONUS_NOT_AVAILABLE).
                const coins = computeDayReward(draft, d, division).granted.coins;
                const bonus = coins > 0 ? coinBonus(coins, draft.coinBonusPercent) : 0;
                return bonus > 0 ? `+${fmt(bonus)}` : '—';
              },
            },
            {
              title: 'Problems',
              render: (_v, _d, i) => {
                const own = errorsUnder(errors, `days[${i}]`);
                return own.length > 0 ? (
                  <Text type="danger" style={{ fontSize: 12 }}>
                    {own.join('; ')}
                  </Text>
                ) : null;
              },
            },
          ]}
        />
      </Card>

      <Card size="small" title="Day 28 Legendary card">
        <Day28CardPicker />
      </Card>

      <Card size="small" title="Rewarded-video coin bonus">
        <Space direction="vertical" size="small">
          <LabeledNumber
            label="Coin bonus"
            path="coinBonusPercent"
            value={draft.coinBonusPercent}
            min={0}
            max={1000}
            addonAfter="%"
            help="0 to 1000. Once per day, after the claim, only if the claim granted coins."
            onChange={(v) =>
              update((d) => {
                d.coinBonusPercent = v;
              })
            }
          />
          <Text type="secondary">
            Bonus = round down (bonus % × the coins the day granted), worked out after the
            multiplier and the cap; the bonus itself is not capped. Example: 1,500 coins granted
            gives +{fmt(coinBonus(1500, draft.coinBonusPercent))}.
          </Text>
        </Space>
      </Card>
    </Space>
  );
};
